import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const HtmlBlock = ({ html, emptyLabel }) => {
  if (html === null || html === undefined || html === "") {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel || "No content available."}
      </Typography>
    );
  }

  if (typeof html !== "string") {
    return <Typography variant="body2">{String(html)}</Typography>;
  }

  return (
    <Box
      sx={{
        fontSize: "0.95rem",
        lineHeight: 1.6,
        "& img": { maxWidth: "100%", height: "auto" },
        "& audio": { width: "100%" },
        "& video": { maxWidth: "100%" },
        "& p:first-of-type": { mt: 0 },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const OptionPreview = ({ option, index }) => {
  const label =
    option?.Value ?? option?.value ?? option?.Name ?? option?.name ?? "";
  const rationale =
    option?.Rationale ?? option?.rationale ?? option?.Feedback ?? "";
  const isCorrect = Boolean(
    option?.IsCorrect ?? option?.isCorrect ?? option?.Correct ?? option?.correct
  );
  const optionKey = option?.id ?? option?.Id ?? option?.key ?? index;

  return (
    <Box
      key={optionKey}
      sx={{
        borderRadius: 1,
        border: "1px solid",
        borderColor: (theme) =>
          isCorrect ? theme.palette.success.light : theme.palette.divider,
        backgroundColor: (theme) =>
          isCorrect
            ? alpha(theme.palette.success.light, 0.12)
            : theme.palette.background.paper,
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, minWidth: 24, textAlign: "center" }}
        >
          {String.fromCharCode(65 + index)}.
        </Typography>
        <Stack spacing={1} sx={{ flex: 1 }}>
          <HtmlBlock html={label} emptyLabel="No option text provided." />
          {rationale ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Rationale
              </Typography>
              <HtmlBlock html={rationale} emptyLabel="No rationale provided." />
            </Stack>
          ) : null}
        </Stack>
        {isCorrect ? <Chip label="Correct" color="success" size="small" /> : null}
      </Stack>
    </Box>
  );
};

const MatchPreview = ({ pairs, distractors }) => {
  const resolvedPairs = Array.isArray(pairs) ? pairs : [];
  const resolvedDistractors = Array.isArray(distractors) ? distractors : [];
  const hasPairs = resolvedPairs.length > 0;
  const hasDistractors = resolvedDistractors.length > 0;

  if (!hasPairs && !hasDistractors) {
    return (
      <Typography variant="body2" color="text.secondary">
        No matching content configured for this item.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {hasPairs ? (
        <Stack spacing={1.5}>
          {resolvedPairs.map((pair, index) => {
            const key = pair?.id ?? index;

            return (
              <Box
                key={key}
                sx={{
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.divider,
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  backgroundColor: (theme) => theme.palette.action.hover,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "stretch" }}
                  >
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="overline" color="text.secondary">
                        Prompt {index + 1}
                      </Typography>
                      <HtmlBlock
                        html={pair?.Name}
                        emptyLabel="No prompt provided."
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="overline" color="text.secondary">
                        Match
                      </Typography>
                      <HtmlBlock
                        html={pair?.Value}
                        emptyLabel="No match provided."
                      />
                    </Stack>
                  </Stack>
                  {pair?.Rationale ? (
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Rationale
                      </Typography>
                      <HtmlBlock
                        html={pair.Rationale}
                        emptyLabel="No rationale provided."
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}

      {hasDistractors ? (
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            Distractors
          </Typography>
          <Stack spacing={1.5}>
            {resolvedDistractors.map((entry, index) => {
              const key = entry?.id ?? index;

              return (
                <Box
                  key={key}
                  sx={{
                    border: "1px dashed",
                    borderColor: (theme) => theme.palette.divider,
                    borderRadius: 1,
                    px: 2,
                    py: 1.25,
                    backgroundColor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack spacing={0.5}>
                      <Typography variant="overline" color="text.secondary">
                        Option {index + 1}
                      </Typography>
                      <HtmlBlock
                        html={entry?.Value}
                        emptyLabel="No distractor text provided."
                      />
                    </Stack>
                    {entry?.Rationale ? (
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Rationale
                        </Typography>
                        <HtmlBlock
                          html={entry.Rationale}
                          emptyLabel="No rationale provided."
                        />
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};

const MFB_SEGMENT_TYPES = {
  TEXT: "text",
  DROPDOWN: "dropdown",
};

const buildInitialDropdownSelections = (segments) => {
  if (!Array.isArray(segments)) {
    return {};
  }

  return segments.reduce((acc, segment, index) => {
    if (segment?.type !== MFB_SEGMENT_TYPES.DROPDOWN) {
      return acc;
    }

    const key = segment?.id ?? index;
    const options = Array.isArray(segment?.options) ? segment.options : [];
    const candidate =
      typeof segment?.selected === "string" && segment.selected.trim()
        ? segment.selected
        : typeof segment?.defaultValue === "string" && segment.defaultValue.trim()
        ? segment.defaultValue
        : options[0] ?? "";

    acc[key] = candidate;

    return acc;
  }, {});
};

const MfbPreview = ({ segments, direction }) => {
  const normalizedDirection =
    typeof direction === "string" && direction.toUpperCase() === "RTL"
      ? "rtl"
      : "ltr";
  const resolvedSegments = Array.isArray(segments) ? segments : [];
  const [dropdownSelections, setDropdownSelections] = React.useState(() =>
    buildInitialDropdownSelections(resolvedSegments)
  );

  React.useEffect(() => {
    setDropdownSelections(buildInitialDropdownSelections(resolvedSegments));
  }, [resolvedSegments]);

  if (!resolvedSegments.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No sentence configured for this item.
      </Typography>
    );
  }

  return (
    <Box
      dir={normalizedDirection}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: (theme) => theme.palette.divider,
        backgroundColor: (theme) => theme.palette.action.hover,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {resolvedSegments.map((segment, index) => {
        if (segment?.type === MFB_SEGMENT_TYPES.DROPDOWN) {
          const key = segment?.id ?? index;
          const options = Array.isArray(segment?.options)
            ? segment.options
            : [];
          const currentValue = dropdownSelections[key] ?? (options[0] ?? "");

          return (
            <FormControl
              key={`dropdown-${key}`}
              size="small"
              sx={{
                minWidth: 96,
                mx: 0.25,
                my: 0.25,
                display: "inline-flex",
              }}
            >
              <Select
                value={currentValue}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setDropdownSelections((prev) => ({
                    ...prev,
                    [key]: nextValue,
                  }));
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: { direction: normalizedDirection },
                  },
                }}
                inputProps={{
                  "aria-label": `Select option ${index + 1}`,
                }}
                sx={{
                  minWidth: 96,
                  "& .MuiSelect-select": {
                    py: 0.75,
                    px: 1.5,
                  },
                }}
              >
                {options.length ? (
                  options.map((option, optionIndex) => (
                    <MenuItem key={`${key}-${optionIndex}`} value={option}>
                      {option || <em>Empty option</em>}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    <em>No options configured</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          );
        }

        const textValue =
          typeof segment?.text === "string"
            ? segment.text
            : segment?.text !== undefined && segment?.text !== null
            ? String(segment.text)
            : "";

        return (
          <Typography
            key={`text-${index}`}
            component="span"
            sx={{
              whiteSpace: "pre-wrap",
              mx: 0.125,
              my: 0.25,
              fontSize: "1rem",
            }}
          >
            {textValue}
          </Typography>
        );
      })}
    </Box>
  );
};

const RearrangePreview = ({ sentences }) => {
  const list = Array.isArray(sentences) ? sentences : [];

  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No sentences configured for this item.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {list.map((sentence, index) => {
        const tokens =
          typeof sentence === "string"
            ? sentence
                .trim()
                .split(/\s+/)
                .filter(Boolean)
            : [];

        return (
          <Stack key={`${index}-${sentence}`} spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Sentence {index + 1}
            </Typography>
            {tokens.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {tokens.map((token, tokenIndex) => (
                  <Chip key={`${index}-${tokenIndex}`} label={token} variant="outlined" />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No words detected in this sentence.
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

const ItemPreviewDialog = ({
  open,
  onClose,
  type,
  name,
  loading,
  error,
  content,
  unavailableMessage,
}) => {
  const normalisedType = type ? String(type).toUpperCase() : "";
  const options = Array.isArray(content?.options) ? content.options : [];
  const segments = Array.isArray(content?.segments) ? content.segments : [];
  const sentences = Array.isArray(content?.sentences) ? content.sentences : [];
  const pairs = Array.isArray(content?.pairs) ? content.pairs : [];
  const distractors = Array.isArray(content?.distractors)
    ? content.distractors
    : [];
  const hasSegments = segments.some((segment) => {
    if (!segment) {
      return false;
    }

    if (segment.type === MFB_SEGMENT_TYPES.DROPDOWN) {
      return (
        Array.isArray(segment.options) &&
        segment.options.some((option) => typeof option === "string" && option.trim())
      );
    }

    if (segment.type === MFB_SEGMENT_TYPES.TEXT) {
      return typeof segment.text === "string" && segment.text.trim();
    }

    return false;
  });
  const hasSentences = sentences.some(
    (sentence) => typeof sentence === "string" && sentence.trim()
  );
  const hasPairs = pairs.some((pair) => {
    if (!pair) {
      return false;
    }

    const name = typeof pair.Name === "string" ? pair.Name.trim() : "";
    const value = typeof pair.Value === "string" ? pair.Value.trim() : "";

    return Boolean(name || value);
  });
  const hasDistractors = distractors.some(
    (entry) => typeof entry?.Value === "string" && entry.Value.trim()
  );
  const hasContent =
    Boolean(content?.title) ||
    options.length > 0 ||
    hasSegments ||
    hasSentences ||
    hasPairs ||
    hasDistractors;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5} alignItems="flex-start">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {name || "Item preview"}
          </Typography>
          {type ? <Chip label={`${type} item`} size="small" variant="outlined" /> : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ py: 1 }}>
          {loading ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Loading preview…
              </Typography>
            </Stack>
          ) : null}

          {!loading && error ? <Alert severity="error">{error}</Alert> : null}

          {!loading && !error && unavailableMessage && !hasContent ? (
            <Alert severity="info">{unavailableMessage}</Alert>
          ) : null}

          {!loading && !error && normalisedType === "MCMA" && hasContent ? (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Prompt
                </Typography>
                <HtmlBlock html={content?.title} emptyLabel="No prompt provided." />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Options
                </Typography>
                {options.length ? (
                  <Stack spacing={1.5}>
                    {options.map((option, index) => (
                      <OptionPreview option={option} index={index} key={option?.id ?? option?.Id ?? index} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No options configured for this item.
                  </Typography>
                )}
              </Stack>
            </Stack>
          ) : null}

          {!loading && !error && normalisedType === "MCMA" && !hasContent && !unavailableMessage ? (
            <Alert severity="info">Preview data is not available for this item.</Alert>
          ) : null}

          {!loading && !error && normalisedType === "MATCH" && hasContent ? (
            <Stack spacing={3}>
              {content?.title ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prompt
                  </Typography>
                  <HtmlBlock html={content.title} emptyLabel="No prompt provided." />
                </Stack>
              ) : null}

              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Matching set
                </Typography>
                <MatchPreview pairs={pairs} distractors={distractors} />
              </Stack>
            </Stack>
          ) : null}

          {!loading && !error && normalisedType === "MATCH" && !hasContent && !unavailableMessage ? (
            <Alert severity="info">Preview data is not available for this item.</Alert>
          ) : null}

          {!loading && !error && normalisedType === "MFB" && hasContent ? (
            <Stack spacing={3}>
              {content?.title ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prompt
                  </Typography>
                  <HtmlBlock html={content.title} emptyLabel="No prompt provided." />
                </Stack>
              ) : null}

              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sentence
                </Typography>
                <MfbPreview segments={segments} direction={content?.direction} />
              </Stack>
            </Stack>
          ) : null}

          {!loading && !error && normalisedType === "MFB" && !hasContent && !unavailableMessage ? (
            <Alert severity="info">Preview data is not available for this item.</Alert>
          ) : null}

          {!loading && !error && normalisedType === "REARRANGE" && hasContent ? (
            <Stack spacing={3}>
              {content?.title ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prompt
                  </Typography>
                  <HtmlBlock html={content.title} emptyLabel="No prompt provided." />
                </Stack>
              ) : null}

              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sentences
                </Typography>
                <RearrangePreview sentences={sentences} />
              </Stack>
            </Stack>
          ) : null}

          {!loading && !error && normalisedType === "REARRANGE" && !hasContent && !unavailableMessage ? (
            <Alert severity="info">Preview data is not available for this item.</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemPreviewDialog;
